import { Center, DynamicBox, Flex, VStack } from '@web/components/atoms/Layout'
import { AnimatePresence, motion } from '@web/components/atoms/Transitions'
import { Body2 } from '@web/components/atoms/Typography'
import ErrorBoundary from '@web/components/molecules/ErrorBoundary'
import React, { useMemo, useRef } from 'react'
import { useEffect } from 'react'
import type { DonationForm as DonationFormProps } from '@web/graphql-components'
import { DonationFormProvider, DonationFormStep, FormValues, useDonationFormContext } from './DonationFormProvider'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { Sections, useBrandingValues } from '@web/components/utils/branding/useBrandingsStore'
import { DESKTOP_CONTAINER_QUERY } from '../utils'

type DonationFormShellProps = {
  donationFormProps: DonationFormProps
  steps: React.ReactElement[]
  formValues?: FormValues
  googleMapsApiKey?: string
  children?: React.ReactNode
}

const TEXT_COLOR_TO_HEX: { [key in string]: string } = {
  dark: 'var(--branding-color-text-dark)',
  light: 'var(--branding-color-text-light)',
}

export const DonationFormConsumer = ({ steps, children, donationFormProps }: DonationFormShellProps) => {
  const actions = useDonationFormContext((state) => state.actions)
  const step = useDonationFormContext((state) => state.step)
  const prevStep = useDonationFormContext((state) => state.prevStep)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    actions.updateSettings(donationFormProps)
  }, [donationFormProps])

  const isGoingBack = useMemo(() => {
    if (step === DonationFormStep.UpsellStep && prevStep === DonationFormStep.Step1) {
      return false
    }

    return prevStep > step
  }, [step, prevStep])

  const { styles, mobileStyles } = donationFormProps
  const DynamicContainer = steps.length > 1 ? DynamicBox : React.Fragment
  const textColor = useColorWithOpacity(styles?.textColor, '1', 'dark')
  const buttonColors = useBrandingValues()[Sections.Buttons].buttonColors['Primary']
  const buttonColor = useColorWithOpacity(
    styles?.creditCardBackgroundColor,
    styles?.creditCardBackgroundColorOpacity,
    buttonColors.bgColor,
  )
  const buttonTextColor = useColorWithOpacity(
    styles?.creditCardTextColor,
    styles?.creditCardTextColorOpacity,
    buttonColors.textColor ?? '#FFFFFF',
  )
  const buttonHoverTextColor = useColorWithOpacity(
    styles?.creditCardHoverTextColor,
    styles?.creditCardHoverTextColorOpacity,
    buttonColors.hoverTextColor ?? '#FFFFFF',
  )
  const buttonHoverColor = useColorWithOpacity(
    styles?.creditCardHoverBackgroundColor,
    styles?.creditCardHoverBackgroundColorOpacity,
    buttonColors.hoverBgColor,
  )

  useEffect(() => {
    actions.setDonationFormRef(ref)
  }, [])

  return (
    <Center
      w="100%"
      id="donation-form-element"
      scrollMargin="64px"
      className={`c-component-wrapper u-${donationFormProps.id}`}
      ref={ref}
    >
      <Flex
        data-container
        border="1px solid transparent"
        backgroundColor={styles?.backgroundColor ?? '#E2E8F0'}
        borderRadius={`${styles?.borderRadius}px`}
        borderColor={styles?.borderColor}
        w="100%"
        maxW={styles?.maxWidth ?? '600px'}
        position="relative"
        overflow="hidden"
        container="donation-form / inline-size"
        css={{
          color: TEXT_COLOR_TO_HEX[textColor] ?? textColor ?? 'ge.text.primary',
          ...(styles?.textColor === 'light'
            ? {
                '--chakra-colors-gray-600': 'var(--branding-color-text-light)', // color Chakra UI uses for the form caption
              }
            : {}),
          p: mobileStyles?.padding ?? '16px',
          '@container page (min-width: 600px)': {
            p: styles?.padding ?? '24px 64px',
          },
          '--branding-button-primary-bg-color': buttonColor,
          '--chakra-colors-color-palette-focus-ring': buttonColor,
          '--branding-button-primary-text-color': buttonTextColor,
          '--branding-button-primary-hover-bg-color': buttonHoverColor,
          '--branding-button-primary-hover-text-color': buttonHoverTextColor,
          '& input': {
            bgColor: 'white',
          },
        }}
      >
        {steps.length > 0 && (
          <DynamicContainer>
            <AnimatePresence initial={false}>
              <motion.div
                key={step}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  overflow: 'visible',
                }}
                initial={{ x: isGoingBack ? -600 : 600, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: isGoingBack ? -600 : 600, opacity: 0, position: 'absolute' }}
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
              >
                <VStack gap="24px" w="100%" overflow="visible" p="4px">
                  <ErrorBoundary fallback={<Body2>Something went wrong. Please refresh the page and try again.</Body2>}>
                    {step < 0 ? steps[steps.length + step] : steps[step]}
                  </ErrorBoundary>
                </VStack>
              </motion.div>
            </AnimatePresence>
          </DynamicContainer>
        )}
        {children}
      </Flex>
    </Center>
  )
}

const DonationFormShell = ({
  donationFormProps,
  steps,
  formValues,
  googleMapsApiKey,
  children,
}: DonationFormShellProps) => {
  const accentColor = useColorWithOpacity(
    donationFormProps.styles?.accentColor,
    donationFormProps.styles?.accentColorOpacity ?? '1',
    'var(--branding-color-brand)',
  )
  const backgroundColor = useColorWithOpacity(
    donationFormProps.styles?.backgroundColor,
    donationFormProps.styles?.backgroundColorOpacity,
  )

  const borderColor = useColorWithOpacity(
    donationFormProps.styles?.borderColor,
    donationFormProps.styles?.borderColorOpacity,
  )

  const updatedDonationFormProps = {
    ...donationFormProps,
    styles: { ...donationFormProps.styles, accentColor, backgroundColor, borderColor },
  }
  return (
    <DonationFormProvider
      settings={updatedDonationFormProps}
      formValues={formValues}
      googleMapsApiKey={googleMapsApiKey}
    >
      <DonationFormConsumer donationFormProps={updatedDonationFormProps} steps={steps}>
        {children}
      </DonationFormConsumer>
    </DonationFormProvider>
  )
}

export default DonationFormShell
